import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { fetchData } from '../../../helpers/methods';

import FormSelect from '../../../component-library/form-select';
import Button from '../../../component-library/button';
import SpinnerContainer from '../../../component-library/spinner-container';
import Tile from '../../../component-library/tile';

const RequestApiKey = () => {
	const { data, uiMenuConfig } = useOutletContext();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [messagingData, setMessagingData] = useState({});
	const [showSuccess, setShowSuccess] = useState(false);
	const [requestType, setRequestType] = useState('');
	const [selectedConsumer, setSelectedConsumer] = useState('');
	const [requesterNuid, setRequesterNuid] = useState('');
	const [disableSubmit, setDisableSubmit] = useState(true);
	const [disableAtlasId, setDisableAtlasId] = useState(true);
	const [disableRequesterNuid, setDisableRequesterNuid] = useState(true);
	const [requesterNuidData, setRequesterNuidData] = useState([]);
	const [requesterNuidError, setRequesterNuidError] = useState(false);

	const requestTypeData = [
		{ id: 'non-prod', text: 'Non-Prod', value: 'non-prod' },
		// { id: 'psup', text: 'PSUP', value: 'psup' },
		{ id: 'prod', text: 'Prod', value: 'prod' },
	];

	const {
		hideDesignLinks,
	} = data;

	const getValidAtlasAppIds = () => data.designLinks
		.filter(link => {
			const status = link.status?.toLowerCase();
			if (requestType === 'non-prod') {
				return (
					status === 'nonprod-approved' || status === 'approved'
				);
			}
			if (requestType === 'psup') {
				return (
					status === 'nonprod-approved' || status === 'approved'
				);
			}
			if (requestType === 'prod') {
				return (
					status === 'approved'
				);
			}
			return false;
		})
		.filter(link => link.atlasId !== null && link.atlasId !== '')
		.map(link => ({ id: link.designId, text: `${link.atlasId} ${link.designName}`, value: link.designId }))
		.sort((a, b) => a.text.localeCompare(b.text));

	const getValidRequesterNuid = () => {
		const selectedConsumerInteger = Number(selectedConsumer);
		const selectedDesignLink = data.designLinks.find(
			item => item.designId === selectedConsumerInteger,
		);
		let validRequesterNuids = [];
		if (requestType === 'prod') {
			validRequesterNuids = selectedDesignLink?.contacts?.filter(item => item.prod === true);
		} else {
			validRequesterNuids = selectedDesignLink?.contacts?.filter(item => item.nonprod === true);
		}
		if (validRequesterNuids?.length < 1) {
			setRequesterNuidData([]);
			setRequesterNuidError(true);
			setDisableRequesterNuid(true);
		} else {
			const newRequesterNuidData = validRequesterNuids.map(i => ({
				id: i.nuid,
				text: `${i.firstname} ${i.lastname} - ${i.nuid} ${requestType === 'prod' ? i.prodAcronym : i.nonprodAcronym}`,
				value: i.nuid,
			}))
				.sort((a, b) => a.text.localeCompare(b.text));
			setRequesterNuidData(newRequesterNuidData);
		}
	};

	const handleRequestTypeChange = value => {
		setRequestType(value);
		setSelectedConsumer('');
		setRequesterNuid('');
		setRequesterNuidError(false);
	};

	const handleSelectedConsumerChange = value => {
		setSelectedConsumer(value);
		setRequesterNuid('');
		setRequesterNuidError(false);
	};

	const handleSubmit = () => {
		setError('');
		setLoading(true);
		const selectedLink = data.designLinks.find(link => `${link.designId}` === selectedConsumer);
		fetchData({
			uri: '/api/web/kp/accessnow',
			method: 'POST',
			options: {
				body: JSON.stringify({
					specId: data.id,
					atlasId: selectedLink.atlasId,
					requesterNuid,
					environment: requestType,
					designName: selectedLink.designName,
					designId: selectedLink.designId,
				}),
			},
		})
			.then(({ ok, body }) => {
				const { message, status } = body;
				if (ok && status.toLowerCase() === 'success') {
					setShowSuccess(true);
					setDisableSubmit(false);
				} else if (status.toLowerCase() === 'failure') {
					// Status of failure comes from AccessNow
					if (message.startsWith('{')) {
						// The message is a JSON object.
						const parsedMessage = JSON.parse(message);
						setError(`The request failed for the following reason: ${parsedMessage.message}`);
					} else {
						// Not a JSON object, so just display the message.
						setError(`The request failed for the following reason: ${message}`);
					}
					setDisableSubmit(false);
				} else {
					// Any other status implies the connection to AccessNow is down.
					setError('This service is unavailable at this time. Please contact AMP Support if the issue does not resolve on its own.');
					setDisableSubmit(false);
				}
				setLoading(false);
			})
			.catch(() => {
				setError('Sorry, your request could not be sent at this time, please try again.');
				setDisableSubmit(false);
				setLoading(false);
			});
	};

	const getMessagingData = () => {
		fetchData({
			uri: '/api/web/kp/settings',
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					setMessagingData(body);
				}
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		if (requestType) {
			setDisableAtlasId(false);
		} else {
			setDisableAtlasId(true);
		}
	}, [requestType]);

	useEffect(() => {
		if (selectedConsumer) {
			setDisableRequesterNuid(false);
			getValidRequesterNuid();
		} else {
			setDisableRequesterNuid(true);
		}
	}, [selectedConsumer]);

	useEffect(() => {
		if (requestType && selectedConsumer && requesterNuid) {
			setDisableSubmit(false);
		} else {
			setDisableSubmit(true);
		}
	}, [requestType, selectedConsumer, requesterNuid]);

	useEffect(() => {
		getMessagingData();
	}, []);

	return (
		<>
			<h3>{uiMenuConfig?.requestApiKey?.label || 'Get API Access'}</h3>
			<Tile className="margin-bottom-24">
				{loading && (
					<SpinnerContainer />
				)}
				{showSuccess && (
					<>
						{messagingData?.SuccessMessage?.length > 0 ? (
							<p>{messagingData.SuccessMessage}</p>
						) : (
							<p>Thank you for submitting your request.</p>
						)}
					</>
				)}
				{(!loading && hideDesignLinks) && (
					<>
						<p>{messagingData.alternativeMessage}</p>
						<p className="margin-bottom-none">
							<a
								href={messagingData.alternativeUrl}
								target="_blank"
								rel="noreferrer"
								className="text-link"
							>
								{messagingData.alternativeUrl}
							</a>
						</p>
					</>
				)}
				{(!loading && !showSuccess && !hideDesignLinks) && (
					<>
						{messagingData?.formTopMessage?.length > 0 && (
							<p>{messagingData.formTopMessage}</p>
						)}
						<div className="get-api-access__form-row-2-col">
							<div>
								<FormSelect
									action={e => handleRequestTypeChange(e.target.value)}
									data={requestTypeData}
									id="sel-environment"
									value={requestType}
									label={`${messagingData?.formFieldLabels?.selectEnvironment?.length > 0 ? messagingData.formFieldLabels.selectEnvironment : 'Select the environment'}`}
									placeholder="Select Environment"
									required
									fullWidth
								/>
							</div>
							<div>
								<FormSelect
									action={e => handleSelectedConsumerChange(e.target.value)}
									data={getValidAtlasAppIds()}
									id="sel-atlasId"
									value={selectedConsumer}
									label={`${messagingData?.formFieldLabels?.selectConsumer?.length > 0 ? messagingData.formFieldLabels.selectConsumer : 'Select the consumer'}`}
									placeholder="Select Consumer"
									required
									fullWidth
									disabled={disableAtlasId}
								/>
							</div>
						</div>
						<FormSelect
							action={e => setRequesterNuid(e.target.value)}
							data={requesterNuidData}
							id="sel-requesterNuid"
							value={requesterNuid}
							label={`${messagingData?.formFieldLabels?.selectNuid?.length > 0 ? messagingData.formFieldLabels.selectNuid : 'Select the ATLAS Technical Lead or Business Application Owner'}`}
							placeholder="Please Select"
							required
							fullWidth
							error={requesterNuidError ? 'Not Available - ATLAS record incomplete' : ''}
							disabled={disableRequesterNuid}
						/>
						{messagingData?.clientIdMessage?.title?.length > 0 && (
							<p><strong>{messagingData.clientIdMessage.title}</strong></p>
						)}
						{messagingData?.clientIdMessage?.guidelines?.length > 0 && (
							<ul className="bulleted-list border-bottom padding-bottom-16">
								{messagingData.clientIdMessage.guidelines.map((item, index) => (
									<li
										// eslint-disable-next-line react/no-array-index-key
										key={index}
									>
										{item}
									</li>
								))}
							</ul>
						)}
						{messagingData?.formBottomMessage?.additionalGuidelines?.length > 0 && (
							<ul className="bulleted-list">
								{messagingData.formBottomMessage.additionalGuidelines.map((item, index) => (
									<li
										// eslint-disable-next-line react/no-array-index-key
										key={index}
									>
										{item}
									</li>
								))}
							</ul>
						)}
						{error && (
							<p className="font-color-theme-danger">{error}</p>
						)}
						<div className="text-right">
							<Button
								type="primary"
								label="Submit"
								onClick={() => handleSubmit()}
								isDisabled={disableSubmit}
							/>
						</div>
					</>
				)}
			</Tile>
		</>
	);
};

export default RequestApiKey;
